import React, { useState, useCallback } from 'react';
import { Skeleton } from './LoadingStates';

interface ImageWithLoadingProps {
  src: string;
  alt: string;
  className?: string;
  aspectRatio?: 'square' | '4/3' | '16/9' | 'auto';
  fallbackIcon?: string;
  fallbackText?: string;
  onLoad?: () => void;
  onError?: () => void;
  priority?: boolean; // For above-the-fold images
}

export const ImageWithLoading: React.FC<ImageWithLoadingProps> = ({
  src,
  alt,
  className = '',
  aspectRatio = '4/3',
  fallbackIcon = '📦',
  fallbackText = 'No image',
  onLoad,
  onError,
  priority = false
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = useCallback(() => {
    setIsLoading(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  }, [onError]);

  const aspectRatioClasses = {
    'square': 'aspect-square',
    '4/3': 'aspect-[4/3]',
    '16/9': 'aspect-video',
    'auto': ''
  };

  const containerClass = `relative overflow-hidden ${aspectRatioClasses[aspectRatio]} ${className}`;

  if (hasError || !src) {
    return (
      <div className={`${containerClass} bg-teddy-100 flex items-center justify-center`}>
        <div className="text-center">
          <span className="text-4xl text-urban-300 mb-2 block">{fallbackIcon}</span>
          <span className="text-sm text-urban-400">{fallbackText}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {/* Loading skeleton with shimmer */}
      {isLoading && (
        <Skeleton className="absolute inset-0" />
      )}
      
      {/* Actual image */}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleLoad}
        onError={handleError}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
      />
    </div>
  );
};

// Gallery component with loading states
export const ImageGallery: React.FC<{
  images: Array<{ url: string; altText?: string }>;
  className?: string;
  maxImages?: number;
}> = ({ images, className = '', maxImages = 4 }) => {
  const displayImages = images.slice(0, maxImages);
  const remainingCount = Math.max(0, images.length - maxImages);

  if (!images.length) {
    return (
      <div className={`bg-teddy-100 rounded-lg flex items-center justify-center p-8 ${className}`}>
        <div className="text-center">
          <span className="text-4xl text-urban-300 mb-2 block">🖼️</span>
          <span className="text-sm text-urban-400">No images available</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid gap-2 ${className}`}>
      {displayImages.length === 1 && (
        <ImageWithLoading
          src={displayImages[0].url}
          alt={displayImages[0].altText || 'Image'}
          className="rounded-lg"
          priority={true}
        />
      )}
      
      {displayImages.length === 2 && (
        <div className="grid grid-cols-2 gap-2">
          {displayImages.map((image, index) => (
            <ImageWithLoading
              key={index}
              src={image.url}
              alt={image.altText || `Image ${index + 1}`}
              className="rounded-lg"
              priority={index === 0}
            />
          ))}
        </div>
      )}
      
      {displayImages.length >= 3 && (
        <div className="grid grid-cols-2 gap-2">
          <ImageWithLoading
            src={displayImages[0].url}
            alt={displayImages[0].altText || 'Main image'}
            className="row-span-2 rounded-lg"
            priority={true}
          />
          <div className="grid gap-2">
            {displayImages.slice(1, 3).map((image, index) => (
              <ImageWithLoading
                key={index + 1}
                src={image.url}
                alt={image.altText || `Image ${index + 2}`}
                className="rounded-lg"
              />
            ))}
            {remainingCount > 0 && (
              <div className="relative">
                <ImageWithLoading
                  src={displayImages[3]?.url || displayImages[2].url}
                  alt={displayImages[3]?.altText || 'More images'}
                  className="rounded-lg"
                />
                <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">+{remainingCount}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Avatar component with loading state
export const AvatarWithLoading: React.FC<{
  src?: string;
  alt: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fallbackInitials?: string;
}> = ({ src, alt, size = 'md', fallbackInitials }) => {
  const [isLoading, setIsLoading] = useState(!!src);
  const [hasError, setHasError] = useState(false);

  const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-12 h-12 text-sm',
    lg: 'w-16 h-16 text-base',
    xl: 'w-24 h-24 text-xl'
  };

  const handleLoad = () => setIsLoading(false);
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (!src || hasError) {
    return (
      <div className={`${sizeClasses[size]} bg-cardboard-100 rounded-full flex items-center justify-center font-semibold text-cardboard-700`}>
        {fallbackInitials || alt.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <div className={`relative ${sizeClasses[size]} rounded-full overflow-hidden`}>
      {isLoading && (
        <Skeleton className="absolute inset-0 rounded-full" />
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};